import { createContext, useContext } from "react"
import type { User } from "@/types"

/**
 * Payload accepted when creating a new user account.
 */
export interface SignupPayload {
  email: string
  password: string
  fullName: string
  givenName?: string
  familyName?: string
  locale?: string
}

/**
 * Payload accepted when updating the authenticated user's profile.
 */
export interface UpdateProfilePayload {
  name: string
}

/**
 * Authentication state and actions shared throughout the application.
 */
export interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isInitializing: boolean
  /** True when authenticated with backend; false for demo/offline mode */
  isBackendMode: boolean
  login: (
    email: string,
    password: string,
    options?: { redirectHint?: "student" | "instructor"; redirectPath?: string },
  ) => Promise<User>
  logout: () => Promise<void>
  signup: (payload: SignupPayload) => Promise<void>
  updateProfile: (payload: UpdateProfilePayload) => Promise<User>
  refreshUser: () => Promise<User | null>
}

/**
 * React context storing the current authentication information.
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Hook wrapper for accessing the AuthContext with explicit provider enforcement.
 *
 * @throws Error if invoked outside an AuthProvider.
 * @returns The active authentication context value.
 */
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
